import React, { useEffect, useState } from 'react';
import { Breadcrumb } from 'components';
import {
    Grid,
    Typography,
    InputLabel,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Link,
    Stack,
    Snackbar,
    Alert
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import { newClientPriceList, getPriceListTest, getAllTestsInASet } from 'services';
import ContentPasteSearchOutlinedIcon from '@mui/icons-material/ContentPasteSearchOutlined';
import ClearOutlinedIcon from '@mui/icons-material/ClearOutlined';

export const PriceListCatalogue = () => {
    const [newPriceList, setNewPriceList] = useState([] as any[]);
    const [getPriceListId, setGetPriceListId] = useState('');
    const [data, setData] = useState<any>();
    const [expandedRows, setExpandedRows] = useState({});
    const [filters, setFilters] = useState({ name: "", code: "", shortName: "" });
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [noTestFound, setNoTestFound] = useState(false);

    useEffect(() => {
        newClientPriceList().then((res: any) => {
            setNewPriceList(Array.isArray(res?.data) ? res.data : []);
        })
    }, [])

    useEffect(() => {
        if (getPriceListId !== '') {
            fetchParentData(page, filters);
        }
    }, [getPriceListId, page]);

    const fetchParentData = (page: any, filters: any) => {
        getPriceListTest(getPriceListId, page + 1, filters.name, filters.code, filters.shortName).then((res: any) => {
            if (res.status === 200) {
                setData(res?.data?.priceListTests);
                setTotalPages(res?.data?.priceListTestsCount);
                if (res?.data?.priceListTests.length === 0) {
                    setNoTestFound(true);
                    setFilters({ name: "", code: "", shortName: "" });
                }
            } else {
                setError(true);
                setErrorMessage(res?.data);
            }
        });
    }

    const fetchChildData = async (associatedTest: any) => {
        const response = await getAllTestsInASet(associatedTest).then((res: any) => {
            if (res.status === 200) {
                return res?.data;
            } else {
                setError(true);
                setErrorMessage(res.data);
            }
        });
        return response;
    };

    const handleRowClick = async (id: any, matrixtypeid: any, associatedTest: any) => {
        const key = `${id}-${matrixtypeid}`;
        if (expandedRows[key]) {
            setExpandedRows((prev) => {
                const newRows = { ...prev };
                delete newRows[key];
                return newRows;
            });
        } else {
            const childData = await fetchChildData(associatedTest);
            setExpandedRows((prev) => ({ ...prev, [key]: childData }));
        }
    };

    const handleSearch = () => {
        setPage(0);
        fetchParentData(0, filters);
    };

    const handleClear = () => {
        setFilters({ name: "", code: "", shortName: "" });
        setPage(0);
        fetchParentData(0, { name: "", code: "", shortName: "" });
    };

    const handleChangePage = (event: any, newPage: any) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: any) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    let nav: any = [{ name: 'Home', path: '/home' }, { name: 'Catalogue', path: '/home/priceList' }, { name: 'Price List Catalogue', path: '/home/priceList' }];
    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 0', cursor: 'pointer' }}>
                <Breadcrumb navList={nav} />
            </div>
            <Typography variant="h6" gutterBottom align='left' mb={2}>
                Price List Catalogue
            </Typography>
            <Grid container spacing={3} mt={5} ml={0}>
                <Grid item xs={2} sm={2} md={2} style={{ paddingTop: 0, paddingLeft: 0 }}>
                    <InputLabel>
                        <Typography variant="subtitle1" gutterBottom>
                            Price List
                        </Typography>
                    </InputLabel>
                </Grid>
                <Grid item xs={4} sm={4} md={4} style={{ padding: 0 }}>
                    <Autocomplete
                        freeSolo
                        disableClearable
                        size="small"
                        options={Array.isArray(newPriceList) ? newPriceList : []}
                        noOptionsText="No Options"
                        onChange={(event, newValue) => {
                            if (typeof newValue === 'string') {
                                // User typed a custom value and pressed Enter
                                setGetPriceListId('');
                                return;
                            }

                            setGetPriceListId(newValue?.id || '');
                        }}
                        filterOptions={createFilterOptions({
                            stringify: (option) => option.name ?? ''
                        })}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                                return option;
                            }

                            return option?.name ?? '';
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                inputProps={{
                                    ...params.inputProps,
                                    type: 'search'
                                }}
                            />
                        )}
                    />
                </Grid>
            </Grid>
            {totalPages > 0 ?
                <>
                    <Grid container spacing={2} style={{ backgroundColor: '#f5f5f5', marginLeft: '0px', padding: '16px', borderRadius: '6px', width: '100%', marginTop: '32px' }}>
                        <Grid item xs={6} sm={6} md={3} style={{ padding: '0px 8px' }} sx={{ mt: { xs: 2, sm: 1, md: 0 } }}>
                            <TextField
                                label="Test Name"
                                variant="outlined"
                                value={filters.name}
                                size="small"
                                sx={{
                                    '.MuiInputLabel-root': {
                                        fontSize: '12px'
                                    },
                                    width: '100%'
                                }}
                                onChange={(e) => setFilters({ ...filters, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6} md={3} style={{ padding: '0px 8px' }} sx={{ mt: { xs: 2, sm: 1, md: 0 } }}>
                            <TextField
                                label="Code"
                                variant="outlined"
                                value={filters.code}
                                size="small"
                                sx={{
                                    '.MuiInputLabel-root': {
                                        fontSize: '12px'
                                    },
                                    width: '100%'
                                }}
                                onChange={(e) => setFilters({ ...filters, code: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6} md={3} style={{ padding: '0px 8px' }} sx={{ mt: { xs: 2, sm: 1, md: 0 } }}>
                            <TextField
                                label="Short Name"
                                variant="outlined"
                                value={filters.shortName}
                                size="small"
                                sx={{
                                    '.MuiInputLabel-root': {
                                        fontSize: '12px'
                                    },
                                    width: '100%'
                                }}
                                onChange={(e) => setFilters({ ...filters, shortName: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} style={{ padding: '0px 8px' }} sx={{ mt: { xs: 2, sm: 1, md: 0 } }}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<ContentPasteSearchOutlinedIcon />}
                                onClick={handleSearch}
                            >
                                Search
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<ClearOutlinedIcon />}
                                onClick={handleClear}
                                sx={{ marginLeft: '8px' }}
                            >
                                Clear All
                            </Button>
                        </Grid>
                    </Grid>
                    <TableContainer>
                        <Table sx={{ border: '1px solid #ddd', marginTop: '32px' }}>
                            <TableHead style={{ backgroundColor: '#003883' }}>
                                <TableRow>
                                    <TableCell style={{ color: '#fff', width: '150px' }}>Test Name</TableCell>
                                    <TableCell style={{ color: '#fff', width: '56px' }}>Code</TableCell>
                                    <TableCell style={{ color: '#fff', width: '56px' }}>Short Name</TableCell>
                                    <TableCell style={{ color: '#fff', width: '56px' }}>Matrix Type</TableCell>
                                    <TableCell style={{ color: '#fff', width: '56px' }}>Matrix</TableCell>
                                    <TableCell style={{ color: '#fff', width: '56px' }}>Test Price</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.map((row) => (
                                    <React.Fragment key={`${row.id}-${row.matrixTypeID}`}>
                                        <TableRow>
                                            <TableCell>
                                                {row.isset ? (
                                                    <Link
                                                        href="#"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            handleRowClick(row.id, row.matrixTypeID, row.associatedTests);
                                                        }}
                                                    >
                                                        {row.name}
                                                    </Link>
                                                ) : (
                                                    row.name
                                                )}
                                            </TableCell>
                                            <TableCell>{row.code}</TableCell>
                                            <TableCell>{row.shortName}</TableCell>
                                            <TableCell>{row.matrixType}</TableCell>
                                            <TableCell>{row.matrix}</TableCell>
                                            <TableCell>{row.testPrice}</TableCell>
                                        </TableRow>
                                        {expandedRows[`${row.id}-${row.matrixTypeID}`] &&
                                            expandedRows[`${row.id}-${row.matrixTypeID}`].map((child) => (
                                                <TableRow key={`${child.id}-${child.matrixTypeID}`} sx={{ backgroundColor: "#f5f5f5" }}>
                                                    <TableCell>{child.name}</TableCell>
                                                    <TableCell>{child.code}</TableCell>
                                                    <TableCell>{child.shortName}</TableCell>
                                                    <TableCell>{child.matrixType}</TableCell>
                                                    <TableCell>{child.matrix}</TableCell>
                                                    <TableCell>{child.testPrice}</TableCell>
                                                </TableRow>
                                            ))}
                                    </React.Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        rowsPerPageOptions={[5, 10]}
                        component="div"
                        count={totalPages}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </> : null
            }
            <Stack sx={{ width: '100%', marginTop: 2 }} spacing={2}>
                <Snackbar open={error} autoHideDuration={6000} onClose={() => setError(false)}>
                    <Alert severity="warning" onClose={() => setError(false)}>{errorMessage}</Alert>
                </Snackbar>
                <Snackbar open={noTestFound} autoHideDuration={6000} onClose={() => setNoTestFound(false)}>
                    <Alert severity="warning" onClose={() => setNoTestFound(false)}>Test not found!</Alert>
                </Snackbar>
            </Stack>
        </div >
    );
}
